import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Component to handle layers control
const LayersControlComponent = () => {
    const map = useMap();

    useEffect(() => {
        // Define base layers
        const esriOcean = L.tileLayer('https://services.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}', {
            attribution: '&copy; <a href="https://www.arcgis.com/">Esri</a>'
        });

        const cartoLight = L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; <a href="https://carto.com/">CARTO</a>'
        });

        // Topographic layers
        const topoTerrain = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 19,
            attribution: 'Terrain &copy; Esri'
        });

        const topoRoads = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 19,
            opacity: 0.7,
            attribution: 'Roads &copy; Esri'
        });

        const topoLabels = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
            maxZoom: 19,
            opacity: 0.7,
            attribution: 'Labels &copy; Esri'
        });

        const openTopoMap = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: 'Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)'
        });

        // Define base maps object
        const baseMaps = {
            "Ocean": esriOcean,
            "Light": cartoLight,
            "Topographic (Full)": openTopoMap,
            "Terrain Only": topoTerrain
        };

        // Define overlay maps
        const overlayMaps = {
            "Roads": topoRoads,
            "Labels": topoLabels
        };

        // Add the default layer
        esriOcean.addTo(map);

        // Add layers control and store reference
        const layerControl = L.control.layers(baseMaps, overlayMaps, {
            collapsed: false,
            position: 'topright'
        }).addTo(map);

        // Cleanup function
        return () => {
            if (map && layerControl) {
                map.removeLayer(esriOcean);
                map.removeLayer(cartoLight);
                map.removeLayer(openTopoMap);
                map.removeLayer(topoTerrain);
                map.removeLayer(topoRoads);
                map.removeLayer(topoLabels);
                layerControl.remove();
            }
        };
    }, [map]);

    return null;
};

const MapDisplay = ({ locations, headers, visualSettings, createColorIcon }) => {
    const getMarkerSize = (data) => {
        if (!visualSettings.sizeBy) return [25, 41];
        
        const sizeIndex = headers.indexOf(visualSettings.sizeBy);
        if (sizeIndex === -1) return [25, 41];

        const value = parseFloat(data[sizeIndex]);
        if (isNaN(value)) return [25, 41];

        const allValues = locations
            .map(loc => parseFloat(loc.data[sizeIndex]))
            .filter(val => !isNaN(val));
        
        const minVal = Math.min(...allValues);
        const maxVal = Math.max(...allValues);
        
        const scale = (value - minVal) / (maxVal - minVal);
        const size = visualSettings.sizeRange.min + 
            (visualSettings.sizeRange.max - visualSettings.sizeRange.min) * scale;
        
        return [size, size * 1.64];
    };

    const getMarkerIcon = (data) => {
        if (!visualSettings.colorBy) return createColorIcon('#3B82F6'); // Default blue
        
        const colorIndex = headers.indexOf(visualSettings.colorBy);
        if (colorIndex === -1) return createColorIcon('#3B82F6');

        const value = data[colorIndex];
        const color = visualSettings.colorRules[value] || '#3B82F6';
        
        return createColorIcon(color);
    };

    if (locations.length === 0) {
        return (
            <div className="h-full w-full flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <svg 
                        className="w-16 h-16 mx-auto mb-4 text-gray-400" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="2"
                    >
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                    <p className="text-gray-500">Add data to visualize on the map</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full w-full">
            <MapContainer 
                center={[locations[0].lat, locations[0].lon]} 
                zoom={13} 
                style={{ height: '100%', width: '100%' }}
            >
                <LayersControlComponent />
                {locations.map((location, index) => {
                    const markerSize = getMarkerSize(location.data);
                    const icon = getMarkerIcon(location.data);
                    
                    // Update icon size based on marker size
                    icon.options.iconSize = markerSize;
                    icon.options.iconAnchor = [markerSize[0]/2, markerSize[1]];
                    icon.options.popupAnchor = [0, -markerSize[1]];

                    return (
                        <Marker 
                            key={index} 
                            position={[location.lat, location.lon]}
                            icon={icon}
                        >
                            <Popup>
                                <div>
                                    <h3 className="font-bold">{location.address}</h3>
                                    <table className="mt-2">
                                        <tbody>
                                            {headers.map((header, i) => (
                                                <tr key={i}>
                                                    <td className="pr-2 font-medium">{header}:</td>
                                                    <td>{location.data[i]}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
};

export default MapDisplay;
